import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { IngredientCombobox } from '../components/ui/ingredient-combobox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Plus, 
  ChefHat,
  Pencil,
  Trash2,
  Search,
  Package,
  Link2,
  Check,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  X,
  DollarSign,
  Utensils
} from 'lucide-react';
import { toast } from 'sonner';

const UNITS = ['kg', 'g', 'L', 'ml', 'each', 'portion', 'bunch', 'can', 'bottle', 'loaf', 'pack', 'tbsp', 'tsp', 'cup'];

const INGREDIENT_CATEGORIES = [
  { value: 'produce', label: 'Produce' },
  { value: 'meat', label: 'Meat' },
  { value: 'seafood', label: 'Seafood' },
  { value: 'dairy', label: 'Dairy' },
  { value: 'dry_goods', label: 'Dry Goods' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'condiments', label: 'Condiments' },
  { value: 'other', label: 'Other' }
];

const RecipesPage = () => {
  const { currentLocation, api } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [modifiers, setModifiers] = useState([]); // NEW: modifiers from Toast
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('menu_items'); // 'menu_items', 'modifiers', or 'sub_recipes'
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'with_recipe', 'without_recipe'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [deleteRecipe, setDeleteRecipe] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  
  // Recipe form
  const [form, setForm] = useState({
    name: '',
    recipe_type: 'menu_item',
    linked_menu_item_id: '',
    yield_quantity: '1',
    yield_unit: 'portion',
    prep_time_minutes: '',
    notes: ''
  });
  
  // Component adding
  const [addingComponent, setAddingComponent] = useState(false);
  const [componentForm, setComponentForm] = useState({
    type: 'ingredient',
    ingredient_id: '',
    sub_recipe_id: '',
    quantity: '',
    unit: 'each'
  });
  
  // Inline ingredient creation
  const [showNewIngredientForm, setShowNewIngredientForm] = useState(false);
  const [newIngredientForm, setNewIngredientForm] = useState({
    name: '',
    category: 'produce',
    unit: 'kg',
    cost_per_unit: '',
    supplier: ''
  });
  const [creatingIngredient, setCreatingIngredient] = useState(false);
  
  // Modifier Deltas (ingredient cost adjustments for modifiers)
  const [modifierDeltas, setModifierDeltas] = useState([]);
  const [loadingDeltas, setLoadingDeltas] = useState(false);
  const [deltaDialogOpen, setDeltaDialogOpen] = useState(false);
  const [editingDelta, setEditingDelta] = useState(null);
  const [deltaForm, setDeltaForm] = useState({
    ingredient_id: '',
    delta_quantity: '',
    unit: 'portion'
  });

  useEffect(() => {
    if (currentLocation) {
      fetchAll();
    }
  }, [currentLocation]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [recipesRes, ingredientsRes, menuItemsRes, modifiersRes] = await Promise.all([
        api.get('/recipes', { params: { location_id: currentLocation.id } }),
        api.get('/ingredients', { params: { location_id: currentLocation.id } }),
        // Use menu-item-masters to get ALL unique items (not limited by sales period)
        api.get('/menu-item-masters', { params: { location_id: currentLocation.id, item_type: 'item' } }),
        // Also fetch modifiers
        api.get('/menu-item-masters', { params: { location_id: currentLocation.id, item_type: 'modifier' } })
      ]);
      setRecipes(recipesRes.data);
      setIngredients(ingredientsRes.data);
      // menu-item-masters returns {items: [...], total_items, ...}
      setMenuItems(menuItemsRes.data.items || []);
      setModifiers(modifiersRes.data.items || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Build a map of menu item ID -> recipe (includes modifiers)
  const menuItemRecipeMap = useMemo(() => {
    const map = {};
    recipes.forEach(recipe => {
      if (recipe.linked_menu_item_id) {
        map[recipe.linked_menu_item_id] = recipe;
      }
    });
    return map;
  }, [recipes]);

  // Get unique sales categories from menu items
  const salesCategories = useMemo(() => {
    const cats = new Set(menuItems.map(item => item.sales_category).filter(Boolean));
    return Array.from(cats).sort();
  }, [menuItems]);

  // Helper to get item price (handles both product-mix and menu-item-masters data)
  const getItemPrice = (item) => item.avg_item_price || item.avg_price || item.list_price || 0;

  // Filter menu items
  const filteredMenuItems = useMemo(() => {
    return menuItems.filter(item => {
      const matchesSearch = item.item_name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || item.sales_category === categoryFilter;
      const hasRecipe = !!menuItemRecipeMap[item.id];
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'with_recipe' && hasRecipe) ||
        (statusFilter === 'without_recipe' && !hasRecipe);
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [menuItems, searchQuery, categoryFilter, statusFilter, menuItemRecipeMap]);

  // Sub-recipes (recipes not linked to menu items)
  const subRecipes = useMemo(() => {
    return recipes.filter(r => r.recipe_type === 'sub_recipe');
  }, [recipes]);

  const filteredSubRecipes = useMemo(() => {
    return subRecipes.filter(r => 
      r.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [subRecipes, searchQuery]);

  // Filter modifiers — group by parent item
  const filteredModifiers = useMemo(() => {
    return modifiers.filter(item => {
      const matchesSearch = item.item_name.toLowerCase().includes(searchQuery.toLowerCase())
        || (item.parent_item_name || '').toLowerCase().includes(searchQuery.toLowerCase());
      const hasRecipe = !!menuItemRecipeMap[item.id];
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'with_recipe' && hasRecipe) ||
        (statusFilter === 'without_recipe' && !hasRecipe);
      return matchesSearch && matchesStatus;
    });
  }, [modifiers, searchQuery, statusFilter, menuItemRecipeMap]);

  // Group modifiers by parent item for the modifiers tab
  const groupedModifiers = useMemo(() => {
    const groups = {};
    filteredModifiers.forEach(mod => {
      const parentName = mod.parent_item_name || 'Other';
      if (!groups[parentName]) groups[parentName] = [];
      groups[parentName].push(mod);
    });
    // Sort groups alphabetically
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredModifiers]);

  // Stats
  const stats = useMemo(() => {
    const totalMenuItems = menuItems.length;
    const withRecipes = menuItems.filter(item => menuItemRecipeMap[item.id]).length;
    const withoutRecipes = totalMenuItems - withRecipes;
    
    // Modifier stats
    const totalModifiers = modifiers.length;
    const modifiersWithRecipes = modifiers.filter(item => menuItemRecipeMap[item.id]).length;
    const modifiersWithoutRecipes = totalModifiers - modifiersWithRecipes;
    
    // Use avg_item_price (Toast base price) not avg_price (includes modifiers)
    const avgFoodCostPct = withRecipes > 0 
      ? menuItems
          .filter(item => menuItemRecipeMap[item.id] && getItemPrice(item) > 0)
          .reduce((sum, item) => {
            const recipe = menuItemRecipeMap[item.id];
            const basePrice = getItemPrice(item);
            return sum + ((recipe.cost_per_unit || 0) / basePrice * 100);
          }, 0) / withRecipes
      : 0;
    
    return {
      totalMenuItems,
      withRecipes,
      withoutRecipes,
      totalModifiers,
      modifiersWithRecipes,
      modifiersWithoutRecipes,
      subRecipeCount: subRecipes.length,
      avgFoodCostPct
    };
  }, [menuItems, modifiers, menuItemRecipeMap, subRecipes]);

  const handleCreateRecipeForMenuItem = (menuItem, isModifier = false) => {
    setSelectedMenuItem(menuItem);
    setEditingRecipe(null);
    setForm({
      name: menuItem.item_name,
      recipe_type: 'menu_item',  // Both items and modifiers are sellable menu items
      linked_menu_item_id: menuItem.id,
      yield_quantity: '1',
      yield_unit: 'portion',
      prep_time_minutes: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleEditRecipeForMenuItem = (menuItem) => {
    const recipe = menuItemRecipeMap[menuItem.id];
    if (recipe) {
      setSelectedMenuItem(menuItem);
      setEditingRecipe(recipe);
      setForm({
        name: recipe.name,
        recipe_type: recipe.recipe_type,
        linked_menu_item_id: recipe.linked_menu_item_id || '',
        yield_quantity: recipe.yield_quantity?.toString() || '1',
        yield_unit: recipe.yield_unit || 'portion',
        prep_time_minutes: recipe.prep_time_minutes?.toString() || '',
        notes: recipe.notes || ''
      });
      setSelectedRecipe(recipe);
      setDialogOpen(true);
    }
  };

  const handleOpenSubRecipeDialog = (recipe = null) => {
    setSelectedMenuItem(null);
    if (recipe) {
      setEditingRecipe(recipe);
      setForm({
        name: recipe.name,
        recipe_type: 'sub_recipe',
        linked_menu_item_id: '',
        yield_quantity: recipe.yield_quantity?.toString() || '1',
        yield_unit: recipe.yield_unit || 'portion',
        prep_time_minutes: recipe.prep_time_minutes?.toString() || '',
        notes: recipe.notes || ''
      });
    } else {
      setEditingRecipe(null);
      setForm({
        name: '',
        recipe_type: 'sub_recipe',
        linked_menu_item_id: '',
        yield_quantity: '1',
        yield_unit: 'portion',
        prep_time_minutes: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  // ── Modifier Delta Management ──
  
  // Fetch deltas for the selected modifier
  const fetchModifierDeltas = async (modifierId) => {
    if (!modifierId) {
      setModifierDeltas([]);
      return;
    }
    setLoadingDeltas(true);
    try {
      const res = await api.get('/modifier-deltas', { params: { modifier_id: modifierId } });
      setModifierDeltas(res.data || []);
    } catch (err) {
      console.error('Error fetching modifier deltas:', err);
      setModifierDeltas([]);
    } finally {
      setLoadingDeltas(false);
    }
  };
  
  // When a modifier is selected, also fetch its deltas
  useEffect(() => {
    if (mainTab === 'modifiers' && selectedMenuItem?.id) {
      fetchModifierDeltas(selectedMenuItem.id);
    } else {
      setModifierDeltas([]);
    }
  }, [selectedMenuItem, mainTab]);
  
  const handleOpenDeltaDialog = (delta = null) => {
    if (delta) {
      setEditingDelta(delta);
      setDeltaForm({
        ingredient_id: delta.ingredient_id,
        delta_quantity: delta.delta_quantity.toString(),
        unit: delta.unit
      });
    } else {
      setEditingDelta(null);
      setDeltaForm({
        ingredient_id: '',
        delta_quantity: '',
        unit: 'portion'
      });
    }
    setDeltaDialogOpen(true);
  };
  
  const handleSaveDelta = async () => {
    if (!deltaForm.ingredient_id) {
      toast.error('Please select an ingredient');
      return;
    }
    if (!deltaForm.delta_quantity || parseFloat(deltaForm.delta_quantity) === 0) {
      toast.error('Please enter a non-zero quantity (negative to remove cost)');
      return;
    }
    
    try {
      if (editingDelta) {
        // Update existing
        await api.put(`/modifier-deltas/${editingDelta.id}`, {
          delta_quantity: parseFloat(deltaForm.delta_quantity),
          unit: deltaForm.unit
        });
        toast.success('Cost adjustment updated');
      } else {
        // Create new
        await api.post('/modifier-deltas', {
          modifier_id: selectedMenuItem.id,
          ingredient_id: deltaForm.ingredient_id,
          delta_quantity: parseFloat(deltaForm.delta_quantity),
          unit: deltaForm.unit
        });
        toast.success('Cost adjustment added');
      }
      setDeltaDialogOpen(false);
      fetchModifierDeltas(selectedMenuItem.id);
    } catch (err) {
      console.error('Error saving delta:', err);
      const msg = err.response?.data?.detail || 'Failed to save cost adjustment';
      toast.error(msg);
    }
  };
  
  const handleDeleteDelta = async (deltaId) => {
    if (!confirm('Delete this cost adjustment?')) return;
    try {
      await api.delete(`/modifier-deltas/${deltaId}`);
      toast.success('Cost adjustment deleted');
      fetchModifierDeltas(selectedMenuItem.id);
    } catch (err) {
      console.error('Error deleting delta:', err);
      toast.error('Failed to delete');
    }
  };
  
  // Calculate total delta cost impact
  const totalDeltaCost = useMemo(() => {
    return modifierDeltas.reduce((sum, d) => sum + (d.cost_impact || 0), 0);
  }, [modifierDeltas]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a recipe name');
      return;
    }

    try {
      const data = {
        ...form,
        location_id: currentLocation.id,
        yield_quantity: parseFloat(form.yield_quantity) || 1,
        prep_time_minutes: form.prep_time_minutes ? parseInt(form.prep_time_minutes) : null,
        linked_menu_item_id: form.linked_menu_item_id || null
      };

      let newRecipe;
      if (editingRecipe) {
        await api.put(`/recipes/${editingRecipe.id}`, data);
        toast.success('Recipe updated');
        newRecipe = { ...editingRecipe, ...data };
      } else {
        const res = await api.post('/recipes', data);
        toast.success('Recipe created - now add ingredients!');
        newRecipe = res.data;
      }
      
      setDialogOpen(false);
      await fetchAll();
      
      // Auto-select the recipe for adding components
      if (newRecipe) {
        const updated = await api.get(`/recipes/${newRecipe.id}`);
        setSelectedRecipe(updated.data);
      }
    } catch (err) {
      console.error('Error saving recipe:', err);
      toast.error('Failed to save recipe');
    }
  };

  const handleDelete = async () => {
    if (!deleteRecipe) return;
    try {
      await api.delete(`/recipes/${deleteRecipe.id}`);
      toast.success('Recipe deleted');
      setDeleteRecipe(null);
      if (selectedRecipe?.id === deleteRecipe.id) {
        setSelectedRecipe(null);
      }
      fetchAll();
    } catch (err) {
      console.error('Error deleting recipe:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete recipe');
    }
  };

  const handleAddComponent = async () => {
    if (!selectedRecipe) return;
    
    if (componentForm.type === 'ingredient' && !componentForm.ingredient_id) {
      toast.error('Please select an ingredient');
      return;
    }
    if (componentForm.type === 'sub_recipe' && !componentForm.sub_recipe_id) {
      toast.error('Please select a sub-recipe');
      return;
    }
    if (!componentForm.quantity || parseFloat(componentForm.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await api.post(`/recipes/${selectedRecipe.id}/components`, {
        ingredient_id: componentForm.type === 'ingredient' ? componentForm.ingredient_id : null,
        sub_recipe_id: componentForm.type === 'sub_recipe' ? componentForm.sub_recipe_id : null,
        quantity: parseFloat(componentForm.quantity),
        unit: componentForm.unit
      });
      
      toast.success('Component added');
      setAddingComponent(false);
      setComponentForm({ type: 'ingredient', ingredient_id: '', sub_recipe_id: '', quantity: '', unit: 'each' });
      
      // Refresh
      await fetchAll();
      const res = await api.get(`/recipes/${selectedRecipe.id}`);
      setSelectedRecipe(res.data);
    } catch (err) {
      console.error('Error adding component:', err);
      toast.error('Failed to add component');
    }
  };

  const handleRemoveComponent = async (componentId) => {
    if (!selectedRecipe) return;
    try {
      await api.delete(`/recipes/${selectedRecipe.id}/components/${componentId}`);
      toast.success('Component removed');
      
      await fetchAll();
      const res = await api.get(`/recipes/${selectedRecipe.id}`);
      setSelectedRecipe(res.data);
    } catch (err) {
      console.error('Error removing component:', err);
      toast.error('Failed to remove component');
    }
  };

  const handleCreateIngredientInline = async () => {
    if (!newIngredientForm.name.trim()) {
      toast.error('Please enter ingredient name');
      return;
    }
    if (!newIngredientForm.cost_per_unit || parseFloat(newIngredientForm.cost_per_unit) <= 0) {
      toast.error('Please enter a valid cost');
      return;
    }
    
    setCreatingIngredient(true);
    try {
      const data = {
        location_id: currentLocation.id,
        name: newIngredientForm.name.trim(),
        category: newIngredientForm.category,
        unit: newIngredientForm.unit,
        cost_per_unit: parseFloat(newIngredientForm.cost_per_unit),
        supplier: newIngredientForm.supplier.trim() || null,
        notes: null
      };
      
      const res = await api.post('/ingredients', data);
      toast.success(`Ingredient "${data.name}" created!`);
      
      // Add the new ingredient to local state
      setIngredients(prev => [...prev, res.data]);
      
      // Auto-select the new ingredient
      setComponentForm(prev => ({
        ...prev,
        ingredient_id: res.data.id,
        unit: res.data.unit
      }));
      
      // Reset and hide the form
      setShowNewIngredientForm(false);
      setNewIngredientForm({
        name: '',
        category: 'produce',
        unit: 'kg',
        cost_per_unit: '',
        supplier: ''
      });
    } catch (err) {
      console.error('Error creating ingredient:', err);
      toast.error(err.response?.data?.detail || 'Failed to create ingredient');
    } finally {
      setCreatingIngredient(false);
    }
  };

  const formatCurrency = (val) => `£${(val || 0).toFixed(2)}`;
  
  // Round up to 2 decimal places for individual component costs
  // This ensures visual consistency (e.g., £0.075 shows as £0.08, not £0.07)
  const formatCurrencyUp = (val) => {
    const rounded = Math.ceil((val || 0) * 100) / 100;
    return `£${rounded.toFixed(2)}`;
  };

  const getAvailableSubRecipes = () => {
    return subRecipes.filter(r => r.id !== selectedRecipe?.id);
  };

  const getProfitInfo = (menuItem) => {
    const recipe = menuItemRecipeMap[menuItem.id];
    // Use avg_item_price (Toast base price) as source of truth
    const basePrice = getItemPrice(menuItem);
    if (!recipe || !basePrice) return null;
    
    const cost = recipe.cost_per_unit || 0;
    const price = basePrice;
    const profit = price - cost;
    const foodCostPct = (cost / price) * 100;
    const marginPct = (profit / price) * 100;
    
    return { cost, price, profit, foodCostPct, marginPct };
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Recipes & Food Costs</h1>
          <p className="text-muted-foreground">Define ingredients for your menu items to track food costs</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Menu Items</CardDescription>
            <CardTitle className="text-2xl">{stats.totalMenuItems}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Modifiers</CardDescription>
            <CardTitle className="text-2xl">{stats.totalModifiers}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-2">
            <CardDescription className="text-green-700">With Recipes</CardDescription>
            <CardTitle className="text-2xl text-green-700">{stats.withRecipes + stats.modifiersWithRecipes}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardDescription className="text-amber-700">Need Recipes</CardDescription>
            <CardTitle className="text-2xl text-amber-700">{stats.withoutRecipes + stats.modifiersWithoutRecipes}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Sub-Recipes</CardDescription>
            <CardTitle className="text-2xl">{stats.subRecipeCount}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList>
          <TabsTrigger value="menu_items" className="gap-2">
            <Utensils className="w-4 h-4" />
            Menu Items
            {stats.withoutRecipes > 0 && (
              <Badge variant="secondary" className="ml-1">{stats.withoutRecipes}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="modifiers" className="gap-2">
            <Plus className="w-4 h-4" />
            Modifiers
            {stats.modifiersWithoutRecipes > 0 && (
              <Badge variant="secondary" className="ml-1">{stats.modifiersWithoutRecipes}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sub_recipes" className="gap-2">
            <ChefHat className="w-4 h-4" />
            Sub-Recipes
          </TabsTrigger>
        </TabsList>

        {/* Menu Items Tab */}
        <TabsContent value="menu_items" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-menu-items"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {salesCategories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Items</SelectItem>
                <SelectItem value="with_recipe">With Recipe</SelectItem>
                <SelectItem value="without_recipe">Need Recipe</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Menu Items List */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Menu Items from your POS</CardTitle>
                <CardDescription>{filteredMenuItems.length} items</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[600px] overflow-y-auto">
                  {filteredMenuItems.length === 0 ? (
                    <div className="py-8 text-center px-4">
                      <Utensils className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">
                        {menuItems.length === 0 
                          ? 'No menu items found. Import a product mix report from your POS.' 
                          : 'No matching items'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredMenuItems.map(item => {
                        const recipe = menuItemRecipeMap[item.id];
                        const profitInfo = getProfitInfo(item);
                        const isSelected = selectedMenuItem?.id === item.id || selectedRecipe?.linked_menu_item_id === item.id;
                        
                        return (
                          <div 
                            key={item.id}
                            className={`p-3 cursor-pointer hover:bg-accent transition-colors ${isSelected ? 'bg-accent' : ''}`}
                            onClick={() => {
                              setSelectedMenuItem(item);
                              if (recipe) {
                                setSelectedRecipe(recipe);
                              } else {
                                setSelectedRecipe(null);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium truncate">{item.item_name}</p>
                                  {recipe ? (
                                    <Check className="w-4 h-4 text-green-600 shrink-0" />
                                  ) : (
                                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                  <span>{formatCurrency(getItemPrice(item))}</span>
                                  {item.sales_category && (
                                    <>
                                      <span>·</span>
                                      <span className="truncate">{item.sales_category}</span>
                                    </>
                                  )}
                                  {item.modifier_count > 0 && (
                                    <>
                                      <span>·</span>
                                      <span className="text-xs">{item.modifier_count} modifier{item.modifier_count > 1 ? 's' : ''}</span>
                                    </>
                                  )}
                                </div>
                                {profitInfo && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <Badge 
                                      variant={profitInfo.foodCostPct <= 30 ? 'default' : profitInfo.foodCostPct <= 35 ? 'secondary' : 'destructive'}
                                      className="text-xs"
                                    >
                                      {profitInfo.foodCostPct.toFixed(0)}% food cost
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recipe Builder */}
            <Card className="lg:col-span-2">
              {selectedMenuItem || selectedRecipe ? (
                <>
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{selectedMenuItem?.item_name || selectedRecipe?.name}</CardTitle>
                        <CardDescription>
                          {selectedMenuItem && (
                            <>
                              Selling price: {formatCurrency(getItemPrice(selectedMenuItem))}
                              {selectedMenuItem.qty_sold > 0 && ` • ${selectedMenuItem.qty_sold} sold`}
                            </>
                          )}
                        </CardDescription>
                      </div>
                      {selectedRecipe ? (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">{formatCurrency(selectedRecipe.cost_per_unit)}</div>
                          <div className="text-sm text-muted-foreground">recipe cost</div>
                          {selectedMenuItem && getItemPrice(selectedMenuItem) > 0 && (
                            <div className="mt-1">
                              <Badge variant={
                                (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 30 ? 'default' :
                                (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 35 ? 'secondary' : 'destructive'
                              }>
                                {formatCurrency(getItemPrice(selectedMenuItem) - selectedRecipe.cost_per_unit)} profit
                              </Badge>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Button onClick={() => handleCreateRecipeForMenuItem(selectedMenuItem)} data-testid="create-recipe-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Create Recipe
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Show modifiers for this item (only on menu_items tab) */}
                    {mainTab === 'menu_items' && selectedMenuItem?.modifiers?.length > 0 && (
                      <div className="mb-4 p-3 rounded-lg bg-muted/30 border">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                          Modifiers ({selectedMenuItem.modifiers.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedMenuItem.modifiers.map(mod => (
                            <Badge
                              key={mod.id}
                              variant={mod.has_recipe ? 'default' : 'outline'}
                              className={`cursor-pointer text-xs ${!mod.has_recipe ? 'text-amber-600 border-amber-300' : ''}`}
                              onClick={() => {
                                const fullMod = modifiers.find(m => m.id === mod.id);
                                if (fullMod) {
                                  setSelectedMenuItem(fullMod);
                                  const recipe = menuItemRecipeMap[fullMod.id];
                                  setSelectedRecipe(recipe || null);
                                  setMainTab('modifiers');
                                }
                              }}
                            >
                              {mod.has_recipe && <CheckCircle2 className="w-3 h-3 mr-1" />}
                              {mod.item_name}
                              {mod.list_price > 0 && ` (+${formatCurrency(mod.list_price)})`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedRecipe ? (
                      <Tabs defaultValue="components">
                        <TabsList className="mb-4">
                          <TabsTrigger value="components">Ingredients</TabsTrigger>
                          <TabsTrigger value="costing">Costing</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="components">
                          {/* Always-visible Quick Add Row */}
                          <Card className="mb-4 border-dashed bg-muted/20">
                            <CardContent className="pt-4 space-y-3">
                              {/* Type Toggle - only show if sub-recipes available */}
                              {getAvailableSubRecipes().length > 0 && (
                                <div className="flex gap-2">
                                  <Button 
                                    variant={componentForm.type === 'ingredient' ? 'default' : 'outline'} 
                                    size="sm"
                                    onClick={() => {
                                      setComponentForm({...componentForm, type: 'ingredient', sub_recipe_id: ''});
                                      setShowNewIngredientForm(false);
                                    }}
                                  >
                                    <Package className="w-4 h-4 mr-1" />
                                    Ingredient
                                  </Button>
                                  <Button 
                                    variant={componentForm.type === 'sub_recipe' ? 'default' : 'outline'} 
                                    size="sm"
                                    onClick={() => {
                                      setComponentForm({...componentForm, type: 'sub_recipe', ingredient_id: ''});
                                      setShowNewIngredientForm(false);
                                    }}
                                  >
                                    <ChefHat className="w-4 h-4 mr-1" />
                                    Sub-Recipe
                                  </Button>
                                </div>
                              )}
                              
                              {/* Inline New Ingredient Form */}
                              {showNewIngredientForm && componentForm.type === 'ingredient' && (
                                <Card className="border-green-200 bg-green-50/30">
                                  <CardContent className="pt-4 space-y-3">
                                    <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                                      <Plus className="w-4 h-4" />
                                      Create New Ingredient
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="space-y-1">
                                        <Label className="text-xs">Name *</Label>
                                        <Input
                                          value={newIngredientForm.name}
                                          onChange={(e) => setNewIngredientForm({...newIngredientForm, name: e.target.value})}
                                          placeholder="e.g., Chicken Breast"
                                          className="h-9"
                                          data-testid="new-ingredient-name"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Category</Label>
                                        <Select 
                                          value={newIngredientForm.category} 
                                          onValueChange={(val) => setNewIngredientForm({...newIngredientForm, category: val})}
                                        >
                                          <SelectTrigger className="h-9">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {INGREDIENT_CATEGORIES.map(cat => (
                                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Cost Per Unit (£) *</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={newIngredientForm.cost_per_unit}
                                          onChange={(e) => setNewIngredientForm({...newIngredientForm, cost_per_unit: e.target.value})}
                                          placeholder="0.00"
                                          className="h-9"
                                          data-testid="new-ingredient-cost"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Unit</Label>
                                        <Select 
                                          value={newIngredientForm.unit} 
                                          onValueChange={(val) => setNewIngredientForm({...newIngredientForm, unit: val})}
                                        >
                                          <SelectTrigger className="h-9">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {UNITS.map(u => (
                                              <SelectItem key={u} value={u}>{u}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">Supplier (optional)</Label>
                                      <Input
                                        value={newIngredientForm.supplier}
                                        onChange={(e) => setNewIngredientForm({...newIngredientForm, supplier: e.target.value})}
                                        placeholder="e.g., Metro, Costco"
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="flex justify-end gap-2">
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => setShowNewIngredientForm(false)}
                                      >
                                        Cancel
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        onClick={handleCreateIngredientInline}
                                        disabled={creatingIngredient}
                                        data-testid="create-ingredient-btn"
                                      >
                                        {creatingIngredient ? 'Creating...' : 'Create & Select'}
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              )}
                              
                              {/* Quick Add Row */}
                              {!showNewIngredientForm && (
                                <div className="flex items-end gap-2">
                                  {componentForm.type === 'ingredient' ? (
                                    <div className="flex-1">
                                      <Label className="text-xs text-muted-foreground mb-1 block">Add ingredient</Label>
                                      <IngredientCombobox
                                        ingredients={ingredients}
                                        value={componentForm.ingredient_id}
                                        onSelect={(val) => setComponentForm({...componentForm, ingredient_id: val})}
                                        onCreateNew={() => setShowNewIngredientForm(true)}
                                        placeholder="Search ingredients..."
                                        formatCurrency={formatCurrency}
                                      />
                                    </div>
                                  ) : (
                                    <div className="flex-1">
                                      <Label className="text-xs text-muted-foreground mb-1 block">Add sub-recipe</Label>
                                      <Select 
                                        value={componentForm.sub_recipe_id} 
                                        onValueChange={(val) => setComponentForm({...componentForm, sub_recipe_id: val})}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select sub-recipe" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {getAvailableSubRecipes().map(sr => (
                                            <SelectItem key={sr.id} value={sr.id}>
                                              {sr.name} ({formatCurrency(sr.cost_per_unit)}/{sr.yield_unit})
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                                  
                                  <div className="w-20">
                                    <Label className="text-xs text-muted-foreground mb-1 block">Qty</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="0"
                                      value={componentForm.quantity}
                                      onChange={(e) => setComponentForm({...componentForm, quantity: e.target.value})}
                                      data-testid="component-quantity"
                                    />
                                  </div>
                                  
                                  <div className="w-24">
                                    <Label className="text-xs text-muted-foreground mb-1 block">Unit</Label>
                                    <Select 
                                      value={componentForm.unit} 
                                      onValueChange={(val) => setComponentForm({...componentForm, unit: val})}
                                    >
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {UNITS.map(u => (
                                          <SelectItem key={u} value={u}>{u}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  
                                  <Button 
                                    size="icon" 
                                    onClick={handleAddComponent} 
                                    disabled={componentForm.type === 'ingredient' ? !componentForm.ingredient_id : !componentForm.sub_recipe_id}
                                    data-testid="confirm-add-component"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                          
                          {/* Components List */}
                          {selectedRecipe.components?.length === 0 ? (
                            <div className="py-8 text-center border rounded-md border-dashed">
                              <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                              <p className="text-muted-foreground">No ingredients yet</p>
                              <p className="text-sm text-muted-foreground">Add ingredients to calculate food cost</p>
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Ingredient</TableHead>
                                  <TableHead>Quantity</TableHead>
                                  <TableHead className="text-right">Cost</TableHead>
                                  <TableHead className="w-10"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {selectedRecipe.components?.map(comp => (
                                  <TableRow key={comp.id}>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        {comp.ingredient_id ? (
                                          <Package className="w-4 h-4 text-muted-foreground" />
                                        ) : (
                                          <ChefHat className="w-4 h-4 text-muted-foreground" />
                                        )}
                                        <span className="font-medium">
                                          {comp.ingredient_name || comp.sub_recipe_name}
                                        </span>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      {comp.quantity} {comp.unit}
                                    </TableCell>
                                    <TableCell className="text-right font-mono">
                                      {formatCurrencyUp(comp.component_cost)}
                                    </TableCell>
                                    <TableCell>
                                      <Button 
                                        size="icon" 
                                        variant="ghost" 
                                        className="h-7 w-7 text-destructive"
                                        onClick={() => handleRemoveComponent(comp.id)}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                                <TableRow className="bg-muted/50 font-medium">
                                  <TableCell colSpan={2}>Total Recipe Cost</TableCell>
                                  <TableCell className="text-right font-mono text-primary">
                                    {formatCurrency(selectedRecipe.total_cost)}
                                  </TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          )}
                          
                          {/* Recipe Actions */}
                          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setEditingRecipe(selectedRecipe);
                                setForm({
                                  name: selectedRecipe.name,
                                  recipe_type: selectedRecipe.recipe_type,
                                  linked_menu_item_id: selectedRecipe.linked_menu_item_id || '',
                                  yield_quantity: selectedRecipe.yield_quantity?.toString() || '1',
                                  yield_unit: selectedRecipe.yield_unit || 'portion',
                                  prep_time_minutes: selectedRecipe.prep_time_minutes?.toString() || '',
                                  notes: selectedRecipe.notes || ''
                                });
                                setDialogOpen(true);
                              }}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Edit Recipe
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-destructive"
                              onClick={() => setDeleteRecipe(selectedRecipe)}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="costing">
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <Card>
                                <CardHeader className="pb-2">
                                  <CardDescription>Recipe Cost</CardDescription>
                                  <CardTitle className="text-xl">{formatCurrency(selectedRecipe.total_cost)}</CardTitle>
                                </CardHeader>
                              </Card>
                              {selectedMenuItem && getItemPrice(selectedMenuItem) > 0 && (
                                <Card className={
                                  (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 30 
                                    ? 'border-green-200 bg-green-50/50' 
                                    : (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 35 
                                    ? 'border-amber-200 bg-amber-50/50' 
                                    : 'border-red-200 bg-red-50/50'
                                }>
                                  <CardHeader className="pb-2">
                                    <CardDescription>Food Cost %</CardDescription>
                                    <CardTitle className="text-xl">
                                      {((selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem)) * 100).toFixed(1)}%
                                    </CardTitle>
                                  </CardHeader>
                                </Card>
                              )}
                            </div>
                            
                            {selectedMenuItem && getItemPrice(selectedMenuItem) > 0 && (
                              <Card>
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-base">Profitability</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-3">
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Selling Price (POS)</span>
                                      <span className="font-mono">{formatCurrency(getItemPrice(selectedMenuItem))}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Food Cost</span>
                                      <span className="font-mono text-destructive">-{formatCurrency(selectedRecipe.cost_per_unit)}</span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t font-medium">
                                      <span>Gross Profit</span>
                                      <span className="font-mono text-green-600">
                                        {formatCurrency(getItemPrice(selectedMenuItem) - selectedRecipe.cost_per_unit)}
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">Profit Margin</span>
                                      <span className="font-mono">
                                        {(((getItemPrice(selectedMenuItem) - selectedRecipe.cost_per_unit) / getItemPrice(selectedMenuItem)) * 100).toFixed(1)}%
                                      </span>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                            
                            {selectedRecipe.components?.length > 0 && (
                              <Card>
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-base">Cost Breakdown</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    {selectedRecipe.components?.map(comp => {
                                      const pct = selectedRecipe.total_cost > 0 
                                        ? (comp.component_cost / selectedRecipe.total_cost) * 100 
                                        : 0;
                                      return (
                                        <div key={comp.id} className="flex items-center gap-3">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex justify-between text-sm mb-1">
                                              <span className="truncate">{comp.ingredient_name || comp.sub_recipe_name}</span>
                                              <span className="font-mono">{formatCurrencyUp(comp.component_cost)}</span>
                                            </div>
                                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                                              <div 
                                                className="h-full bg-primary rounded-full transition-all" 
                                                style={{ width: `${pct}%` }}
                                              />
                                            </div>
                                          </div>
                                          <span className="text-sm text-muted-foreground w-12 text-right">{pct.toFixed(0)}%</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                          </div>
                        </TabsContent>
                      </Tabs>
                    ) : (
                      <div className="py-12 text-center">
                        <ChefHat className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-semibold text-lg mb-2">No Recipe Yet</h3>
                        <p className="text-muted-foreground mb-4">
                          Create a recipe to define what ingredients go into &quot;{selectedMenuItem?.item_name}&quot;
                        </p>
                        <Button onClick={() => handleCreateRecipeForMenuItem(selectedMenuItem)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Create Recipe
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </>
              ) : (
                <div className="flex items-center justify-center h-[400px]">
                  <div className="text-center">
                    <Utensils className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Select a Menu Item</h3>
                    <p className="text-muted-foreground">Choose a menu item to create or edit its recipe</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Modifiers Tab */}
        <TabsContent value="modifiers" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search modifiers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-modifiers"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modifiers</SelectItem>
                <SelectItem value="with_recipe">With Recipe</SelectItem>
                <SelectItem value="without_recipe">Need Recipe</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-sm text-muted-foreground">
            Modifiers are add-ons customers can select (e.g., Extra bacon, Smoked salmon). Track their costs to ensure profitability.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Modifiers List */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Modifiers from your POS</CardTitle>
                <CardDescription>
                  {filteredModifiers.length} modifiers • {stats.modifiersWithRecipes} with recipes
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  {filteredModifiers.length === 0 ? (
                    <div className="py-8 text-center px-4">
                      <Plus className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">
                        {modifiers.length === 0 
                          ? 'No modifiers imported yet. Upload a Product Mix report.' 
                          : 'No matching modifiers found'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {groupedModifiers.map(([parentName, mods]) => (
                        <div key={parentName}>
                          <div className="px-3 py-2 bg-muted/40 border-b sticky top-0">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{parentName}</p>
                          </div>
                          {mods.map(item => {
                        const recipe = menuItemRecipeMap[item.id];
                        const isSelected = selectedMenuItem?.id === item.id;
                        const itemPrice = getItemPrice(item);
                        
                        return (
                          <div
                            key={item.id}
                            className={`p-3 cursor-pointer transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 border-l-4 border-l-primary' 
                                : recipe 
                                  ? 'hover:bg-green-50/50' 
                                  : 'hover:bg-muted/50'
                            }`}
                            onClick={() => {
                              setSelectedMenuItem(item);
                              if (recipe) {
                                setSelectedRecipe(recipe);
                              } else {
                                setSelectedRecipe(null);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{item.item_name}</p>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <span>{formatCurrency(itemPrice)}</span>
                                </div>
                              </div>
                              {recipe ? (
                                <Badge variant="default" className="shrink-0">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Costed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="shrink-0 text-amber-600 border-amber-300">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Need cost
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Modifier Detail / Recipe Panel */}
            <Card className="lg:col-span-2">
              {!selectedMenuItem || mainTab !== 'modifiers' ? (
                <CardContent className="py-12 text-center">
                  <Plus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Select a Modifier</h3>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    Choose a modifier from the list to view or create its recipe. 
                    Most modifiers have just 1-2 ingredients.
                  </p>
                </CardContent>
              ) : (
                <>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{selectedMenuItem?.item_name}</CardTitle>
                        <CardDescription>
                          Selling price: {formatCurrency(getItemPrice(selectedMenuItem))}
                          {selectedMenuItem?.parent_item_name && (
                            <> &middot; Modifier on <span className="font-medium text-foreground">{selectedMenuItem.parent_item_name}</span></>
                          )}
                        </CardDescription>
                      </div>
                      {selectedRecipe && getItemPrice(selectedMenuItem) > 0 && (
                        <Badge 
                          variant={
                            (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 30 ? 'default' :
                            (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 35 ? 'secondary' : 'destructive'
                          }
                        >
                          {formatCurrency(getItemPrice(selectedMenuItem) - selectedRecipe.cost_per_unit)} profit
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {selectedRecipe ? (
                      <div className="space-y-4">
                        {/* Recipe cost summary */}
                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="text-sm text-muted-foreground">Recipe Cost</p>
                            <p className="text-xl font-bold">{formatCurrency(selectedRecipe.cost_per_unit)}</p>
                          </div>
                          {getItemPrice(selectedMenuItem) > 0 && (
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Food Cost %</p>
                              <p className={`text-xl font-bold ${
                                (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 30 
                                  ? 'text-green-600' 
                                  : (selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem) * 100) <= 35 
                                    ? 'text-amber-600' 
                                    : 'text-red-600'
                              }`}>
                                {((selectedRecipe.cost_per_unit / getItemPrice(selectedMenuItem)) * 100).toFixed(1)}%
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Components */}
                        <div>
                          <h4 className="font-medium mb-2">Ingredients</h4>
                          {selectedRecipe.components?.length > 0 ? (
                            <div className="space-y-2">
                              {selectedRecipe.components.map((comp, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                                  <span>{comp.ingredient_name || comp.sub_recipe_name}</span>
                                  <span className="text-sm text-muted-foreground">
                                    {comp.quantity} {comp.unit} • {formatCurrency(comp.cost)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No ingredients added yet</p>
                          )}
                        </div>
                        
                        {/* Cost Adjustments (Deltas) - For modifiers that add/remove ingredients */}
                        <div className="border-t pt-4 mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">Cost Adjustments</h4>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleOpenDeltaDialog()}
                              data-testid="add-delta-btn"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mb-3">
                            Track costs that modifiers add or remove (e.g., "No Bacon" = -£0.50)
                          </p>
                          {loadingDeltas ? (
                            <div className="space-y-2">
                              <Skeleton className="h-10 w-full" />
                            </div>
                          ) : modifierDeltas.length > 0 ? (
                            <div className="space-y-2">
                              {modifierDeltas.map(delta => (
                                <div 
                                  key={delta.id} 
                                  className={`flex items-center justify-between p-2 rounded ${
                                    delta.delta_quantity < 0 ? 'bg-green-50 dark:bg-green-950/20' : 'bg-red-50 dark:bg-red-950/20'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                                      delta.delta_quantity < 0 ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                    }`}>
                                      {delta.delta_quantity < 0 ? 'SAVES' : 'ADDS'}
                                    </span>
                                    <span className="text-sm">{delta.ingredient_name || 'Unknown'}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`text-sm font-medium ${
                                      delta.delta_quantity < 0 ? 'text-green-700' : 'text-red-700'
                                    }`}>
                                      {delta.delta_quantity < 0 ? '' : '+'}{formatCurrency(delta.cost_impact || 0)}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => handleOpenDeltaDialog(delta)}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-destructive"
                                      onClick={() => handleDeleteDelta(delta.id)}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              {/* Total adjustment summary */}
                              <div className="flex items-center justify-between p-2 bg-muted/50 rounded mt-2">
                                <span className="text-sm font-medium">Net Adjustment</span>
                                <span className={`text-sm font-bold ${
                                  totalDeltaCost < 0 ? 'text-green-700' : totalDeltaCost > 0 ? 'text-red-700' : ''
                                }`}>
                                  {totalDeltaCost < 0 ? '' : totalDeltaCost > 0 ? '+' : ''}{formatCurrency(totalDeltaCost)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">
                              No cost adjustments. Use this for modifiers like "No Bacon" or "Extra Cheese".
                            </p>
                          )}
                        </div>
                        
                        <Button 
                          variant="outline" 
                          onClick={() => handleEditRecipeForMenuItem(selectedMenuItem)}
                          className="w-full"
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit Recipe
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                        <h4 className="font-medium mb-2">No Recipe Yet</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Create a recipe to track the cost of &quot;{selectedMenuItem?.item_name}&quot;
                        </p>
                        <Button onClick={() => handleCreateRecipeForMenuItem(selectedMenuItem, true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Create Recipe
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Sub-Recipes Tab */}
        <TabsContent value="sub_recipes" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search sub-recipes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => handleOpenSubRecipeDialog()} data-testid="add-sub-recipe-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Sub-Recipe
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Sub-recipes are reusable components used in multiple menu items (e.g., house sauce, dough base, spice mix)
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sub-Recipe List */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Sub-Recipes</CardTitle>
                <CardDescription>{filteredSubRecipes.length} items</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  {filteredSubRecipes.length === 0 ? (
                    <div className="py-8 text-center px-4">
                      <ChefHat className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">
                        {subRecipes.length === 0 ? 'No sub-recipes yet' : 'No matching sub-recipes'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredSubRecipes.map(recipe => (
                        <div 
                          key={recipe.id}
                          className={`p-3 cursor-pointer hover:bg-accent transition-colors ${selectedRecipe?.id === recipe.id ? 'bg-accent' : ''}`}
                          onClick={() => {
                            setSelectedMenuItem(null);
                            setSelectedRecipe(recipe);
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate">{recipe.name}</p>
                              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                <span>{formatCurrency(recipe.cost_per_unit)}/{recipe.yield_unit}</span>
                                <span>•</span>
                                <span>{recipe.components?.length || 0} ingredients</span>
                              </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleOpenSubRecipeDialog(recipe); 
                                }}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7 text-destructive" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setDeleteRecipe(recipe); 
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Sub-Recipe Builder */}
            <Card className="lg:col-span-2">
              {selectedRecipe && selectedRecipe.recipe_type === 'sub_recipe' ? (
                <>
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle>{selectedRecipe.name}</CardTitle>
                        <CardDescription>
                          Yields {selectedRecipe.yield_quantity} {selectedRecipe.yield_unit}
                          {selectedRecipe.prep_time_minutes && ` • ${selectedRecipe.prep_time_minutes} min prep`}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">{formatCurrency(selectedRecipe.cost_per_unit)}</div>
                        <div className="text-sm text-muted-foreground">per {selectedRecipe.yield_unit}</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Always-visible Quick Add Row */}
                    <Card className="mb-4 border-dashed bg-muted/20">
                      <CardContent className="pt-4 space-y-3">
                        {/* Type Toggle - only show if sub-recipes available */}
                        {getAvailableSubRecipes().length > 0 && (
                          <div className="flex gap-2">
                            <Button 
                              variant={componentForm.type === 'ingredient' ? 'default' : 'outline'} 
                              size="sm"
                              onClick={() => {
                                setComponentForm({...componentForm, type: 'ingredient', sub_recipe_id: ''});
                                setShowNewIngredientForm(false);
                              }}
                            >
                              <Package className="w-4 h-4 mr-1" />
                              Ingredient
                            </Button>
                            <Button 
                              variant={componentForm.type === 'sub_recipe' ? 'default' : 'outline'} 
                              size="sm"
                              onClick={() => {
                                setComponentForm({...componentForm, type: 'sub_recipe', ingredient_id: ''});
                                setShowNewIngredientForm(false);
                              }}
                            >
                              <ChefHat className="w-4 h-4 mr-1" />
                              Sub-Recipe
                            </Button>
                          </div>
                        )}
                        
                        {/* Inline New Ingredient Form */}
                        {showNewIngredientForm && componentForm.type === 'ingredient' && (
                          <Card className="border-green-200 bg-green-50/30">
                            <CardContent className="pt-4 space-y-3">
                              <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
                                <Plus className="w-4 h-4" />
                                Create New Ingredient
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Name *</Label>
                                  <Input
                                    value={newIngredientForm.name}
                                    onChange={(e) => setNewIngredientForm({...newIngredientForm, name: e.target.value})}
                                    placeholder="e.g., Chicken Breast"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Category</Label>
                                  <Select 
                                    value={newIngredientForm.category} 
                                    onValueChange={(val) => setNewIngredientForm({...newIngredientForm, category: val})}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {INGREDIENT_CATEGORIES.map(cat => (
                                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Cost Per Unit (£) *</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newIngredientForm.cost_per_unit}
                                    onChange={(e) => setNewIngredientForm({...newIngredientForm, cost_per_unit: e.target.value})}
                                    placeholder="0.00"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Unit</Label>
                                  <Select 
                                    value={newIngredientForm.unit} 
                                    onValueChange={(val) => setNewIngredientForm({...newIngredientForm, unit: val})}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {UNITS.map(u => (
                                        <SelectItem key={u} value={u}>{u}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => setShowNewIngredientForm(false)}
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={handleCreateIngredientInline}
                                  disabled={creatingIngredient}
                                >
                                  {creatingIngredient ? 'Creating...' : 'Create & Select'}
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Quick Add Row */}
                        {!showNewIngredientForm && (
                          <div className="flex items-end gap-2">
                            {componentForm.type === 'ingredient' ? (
                              <div className="flex-1">
                                <Label className="text-xs text-muted-foreground mb-1 block">Add ingredient</Label>
                                <IngredientCombobox
                                  ingredients={ingredients}
                                  value={componentForm.ingredient_id}
                                  onSelect={(val) => setComponentForm({...componentForm, ingredient_id: val})}
                                  onCreateNew={() => setShowNewIngredientForm(true)}
                                  placeholder="Search ingredients..."
                                  formatCurrency={formatCurrency}
                                />
                              </div>
                            ) : (
                              <div className="flex-1">
                                <Label className="text-xs text-muted-foreground mb-1 block">Add sub-recipe</Label>
                                <Select 
                                  value={componentForm.sub_recipe_id} 
                                  onValueChange={(val) => setComponentForm({...componentForm, sub_recipe_id: val})}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select sub-recipe" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {getAvailableSubRecipes().map(sr => (
                                      <SelectItem key={sr.id} value={sr.id}>
                                        {sr.name} ({formatCurrency(sr.cost_per_unit)}/{sr.yield_unit})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            
                            <div className="w-20">
                              <Label className="text-xs text-muted-foreground mb-1 block">Qty</Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0"
                                value={componentForm.quantity}
                                onChange={(e) => setComponentForm({...componentForm, quantity: e.target.value})}
                              />
                            </div>
                            
                            <div className="w-24">
                              <Label className="text-xs text-muted-foreground mb-1 block">Unit</Label>
                              <Select 
                                value={componentForm.unit} 
                                onValueChange={(val) => setComponentForm({...componentForm, unit: val})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {UNITS.map(u => (
                                    <SelectItem key={u} value={u}>{u}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <Button 
                              size="icon" 
                              onClick={handleAddComponent}
                              disabled={componentForm.type === 'ingredient' ? !componentForm.ingredient_id : !componentForm.sub_recipe_id}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Components List */}
                    {selectedRecipe.components?.length === 0 ? (
                      <div className="py-8 text-center border rounded-md border-dashed">
                        <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No ingredients yet</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Ingredient</TableHead>
                            <TableHead>Quantity</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedRecipe.components?.map(comp => (
                            <TableRow key={comp.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {comp.ingredient_id ? (
                                    <Package className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChefHat className="w-4 h-4 text-muted-foreground" />
                                  )}
                                  <span className="font-medium">
                                    {comp.ingredient_name || comp.sub_recipe_name}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {comp.quantity} {comp.unit}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(comp.component_cost)}
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => handleRemoveComponent(comp.id)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/50 font-medium">
                            <TableCell colSpan={2}>Total Cost</TableCell>
                            <TableCell className="text-right font-mono text-primary">
                              {formatCurrency(selectedRecipe.total_cost)}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </>
              ) : (
                <div className="flex items-center justify-center h-[400px]">
                  <div className="text-center">
                    <ChefHat className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Select a Sub-Recipe</h3>
                    <p className="text-muted-foreground mb-4">Choose a sub-recipe to view and edit</p>
                    <Button onClick={() => handleOpenSubRecipeDialog()}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Sub-Recipe
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Recipe Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingRecipe ? 'Edit Recipe' : selectedMenuItem ? `Recipe for ${selectedMenuItem.item_name}` : 'New Sub-Recipe'}
            </DialogTitle>
            <DialogDescription>
              {selectedMenuItem 
                ? 'Define the ingredients that go into this menu item'
                : 'Create a reusable sub-recipe component'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Recipe Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder={selectedMenuItem ? selectedMenuItem.item_name : 'e.g., House Sauce'}
                data-testid="recipe-name-input"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Yield Quantity</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.yield_quantity}
                  onChange={(e) => setForm({...form, yield_quantity: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Yield Unit</Label>
                <Select value={form.yield_unit} onValueChange={(val) => setForm({...form, yield_unit: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prep Time (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.prep_time_minutes}
                  onChange={(e) => setForm({...form, prep_time_minutes: e.target.value})}
                  placeholder="Optional"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Preparation instructions, variations, etc."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-recipe-btn">
              {editingRecipe ? 'Update' : 'Create'} Recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteRecipe} onOpenChange={() => setDeleteRecipe(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipe</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteRecipe?.name}&quot;? 
              This will also remove all components. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Delta Dialog - Add/Edit cost adjustment for modifier */}
      <Dialog open={deltaDialogOpen} onOpenChange={setDeltaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingDelta ? 'Edit Cost Adjustment' : 'Add Cost Adjustment'}
            </DialogTitle>
            <DialogDescription>
              {selectedMenuItem?.item_name && (
                <>For modifier: <span className="font-medium">{selectedMenuItem.item_name}</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Ingredient</Label>
              <Select
                value={deltaForm.ingredient_id}
                onValueChange={(val) => setDeltaForm(prev => ({ ...prev, ingredient_id: val }))}
                disabled={!!editingDelta}
              >
                <SelectTrigger data-testid="delta-ingredient-select">
                  <SelectValue placeholder="Select ingredient..." />
                </SelectTrigger>
                <SelectContent>
                  {ingredients.map(ing => (
                    <SelectItem key={ing.id} value={ing.id}>
                      {ing.name} ({formatCurrency(ing.cost_per_unit)}/{ing.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingDelta && (
                <p className="text-xs text-muted-foreground">Ingredient cannot be changed. Delete and create new if needed.</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Quantity Change</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.1"
                  value={deltaForm.delta_quantity}
                  onChange={(e) => setDeltaForm(prev => ({ ...prev, delta_quantity: e.target.value }))}
                  placeholder="e.g., -1 or 0.5"
                  className="flex-1"
                  data-testid="delta-quantity-input"
                />
                <Select
                  value={deltaForm.unit}
                  onValueChange={(val) => setDeltaForm(prev => ({ ...prev, unit: val }))}
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                Use <strong>negative</strong> for removals (e.g., -1 for "No Bacon"). 
                Use <strong>positive</strong> for additions (e.g., +1 for "Extra Cheese").
              </p>
            </div>
            
            {/* Preview cost impact */}
            {deltaForm.ingredient_id && deltaForm.delta_quantity && (
              <div className={`p-3 rounded-lg ${
                parseFloat(deltaForm.delta_quantity) < 0 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className="text-sm font-medium">
                  {parseFloat(deltaForm.delta_quantity) < 0 ? 'Cost Saving' : 'Cost Addition'}
                </p>
                <p className={`text-lg font-bold ${
                  parseFloat(deltaForm.delta_quantity) < 0 ? 'text-green-700' : 'text-red-700'
                }`}>
                  {(() => {
                    const ing = ingredients.find(i => i.id === deltaForm.ingredient_id);
                    const cost = ing ? parseFloat(deltaForm.delta_quantity) * ing.cost_per_unit : 0;
                    return `${cost < 0 ? '' : '+'}${formatCurrency(cost)}`;
                  })()}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeltaDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDelta} data-testid="save-delta-btn">
              {editingDelta ? 'Update' : 'Add'} Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecipesPage;
